import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { BreachPeriod, BREACH_COLORS } from './MiniStackedBarChart';

interface Props {
  data: BreachPeriod[];
}

const TYPE_KEYS = Object.keys(BREACH_COLORS) as (keyof typeof BREACH_COLORS)[];

function cellOpacity(val: number, max: number): number {
  if (max === 0 || val === 0) return 0;
  return 0.15 + (val / max) * 0.85;
}

export default function MiniBreachHeatmap({ data }: Props) {
  if (!data.length) return null;

  const maxVal = Math.max(
    1,
    ...data.flatMap(d => TYPE_KEYS.map(k => d[k])),
  );

  const CELL_W = 34;
  const CELL_H = 20;
  const ROW_LABEL_W = 72;
  const COL_LABEL_H = 18;
  const GAP = 2;

  if (Platform.OS !== 'web') {
    return (
      <View style={styles.fallback}>
        {TYPE_KEYS.map(key => (
          <Text key={key} style={styles.fallbackText}>
            {BREACH_COLORS[key].name}: {data.map(d => d[key]).join(' ')}
          </Text>
        ))}
      </View>
    );
  }

  const svgW = ROW_LABEL_W + data.length * (CELL_W + GAP) - GAP;
  const svgH = COL_LABEL_H + TYPE_KEYS.length * (CELL_H + GAP) - GAP;

  return (
    <View>
      {/* @ts-ignore */}
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
        {/* Column labels (months) */}
        {data.map((d, ci) => {
          const x = ROW_LABEL_W + ci * (CELL_W + GAP) + CELL_W / 2;
          return (
            // @ts-ignore
            <text key={ci} x={x} y={COL_LABEL_H - 4} textAnchor="middle"
              fontSize={8} fill="#888">{d.label}</text>
          );
        })}

        {/* Rows */}
        {TYPE_KEYS.map((key, ri) => {
          const meta = BREACH_COLORS[key];
          const y = COL_LABEL_H + ri * (CELL_H + GAP);
          return (
            <g key={key}>
              {/* Colour swatch + row label */}
              {/* @ts-ignore */}
              <rect x={0} y={y + (CELL_H - 8) / 2} width={8} height={8}
                fill={meta.color} rx={2} opacity={0.9} />
              {/* @ts-ignore */}
              <text x={12} y={y + CELL_H / 2 + 3} textAnchor="start"
                fontSize={8} fill="#666">{meta.name}</text>

              {/* Cells */}
              {data.map((d, ci) => {
                const val = d[key];
                const opacity = cellOpacity(val, maxVal);
                const cx = ROW_LABEL_W + ci * (CELL_W + GAP);
                return (
                  <g key={ci}>
                    {/* @ts-ignore */}
                    <rect x={cx} y={y} width={CELL_W} height={CELL_H}
                      fill={val === 0 ? '#F5F5F5' : meta.color}
                      opacity={val === 0 ? 1 : opacity}
                      rx={3} />
                    {val > 0 && (
                      // @ts-ignore
                      <text x={cx + CELL_W / 2} y={y + CELL_H / 2 + 3}
                        textAnchor="middle" fontSize={8}
                        fill={opacity > 0.6 ? '#fff' : '#333'}
                        fontWeight="600">{val}</text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>

    </View>
  );
}

const styles = StyleSheet.create({
  fallback:     { paddingVertical: 4 },
  fallbackText: { fontSize: 10, color: '#999', marginBottom: 2 },
});
